import { Dialog, Transition } from '@headlessui/react';
import { Fragment, useState } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import BlackCross from '../img/BlackCross.svg';
import CountriesList from '../CountriesList';

const validationSchema = Yup.object({
  partnerName: Yup.string()
    .required('Partner name is required')
    .max(100, 'Partner name must be 100 characters or less.'),
  partnerId: Yup.string()
    .required('Partner ID is required')
    .max(50, 'Partner ID must be 50 characters or less.'),
  contactName: Yup.string()
    .required('Your name is required')
    .max(70, 'Your name must be 70 characters or less.'),
  phone: Yup.string()
    .required('Phone number is required')
    .max(30, 'Phone number must be 30 characters or less.'),
  country: Yup.string().required('Country is required'),
  email: Yup.string()
    .email('Invalid email address')
    .required('Email is required')
    .max(70, 'Email must be 70 characters or less.'),
});

const initialValues = {
  partnerName: '',
  partnerId: '',
  contactName: '',
  phone: '',
  country: 'United States of America',
  email: '',
};

const VerifyPartnerModal = ({ isOpen, onClose }) => {
  const [submitError, setSubmitError] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const closeModal = () => {
    setSubmitError(null);
    setSubmitted(false);
    onClose();
  };

  const handleSubmit = async (formData, { resetForm }) => {
    try {
      const recaptcha = await window.grecaptcha.execute(
        process.env.REACT_APP_RECAPTCHA_SITE_KEY,
        { action: 'verify_partner' }
      );
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/mails/verify-partner`,
        { ...formData, recaptcha }
      );
      setSubmitError(null);
      setSubmitted(true);
      resetForm();
    } catch (error) {
      setSubmitError(
        error?.response?.data || 'Could not send the verification request.'
      );
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as='div' className='relative z-20' onClose={closeModal}>
        <Transition.Child
          as={Fragment}
          enter='ease-out duration-300'
          enterFrom='opacity-0'
          enterTo='opacity-100'
          leave='ease-in duration-200'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'
        >
          <div className='fixed inset-0 bg-black bg-opacity-75' />
        </Transition.Child>

        <div className='fixed inset-0 overflow-y-auto'>
          <div className='flex min-h-full items-center justify-center p-4 text-center'>
            <Transition.Child
              as={Fragment}
              enter='ease-out duration-300'
              enterFrom='opacity-0 scale-95'
              enterTo='opacity-100 scale-100'
              leave='ease-in duration-200'
              leaveFrom='opacity-100 scale-100'
              leaveTo='opacity-0 scale-95'
            >
              <Dialog.Panel className='w-full max-w-lg transform overflow-hidden rounded-2xl bg-white p-6 sm:p-8 text-left align-middle shadow-xl transition-all font-manrope'>
                <div className='flex items-start justify-between'>
                  <Dialog.Title
                    as='h3'
                    className='text-xl font-bold text-[#1A1A1A]'
                  >
                    Verify your partner
                  </Dialog.Title>
                  <button
                    type='button'
                    onClick={closeModal}
                    className='ml-4 mt-1'
                  >
                    <img alt='close' src={BlackCross} />
                  </button>
                </div>
                {submitted ? (
                  <div className='mt-6'>
                    <p className='text-sm text-[#6B6B6B] leading-relaxed'>
                      Your request has been sent. We will check the details
                      against our partner registry and reply directly.
                    </p>
                    <div className='mt-8 flex justify-end'>
                      <button
                        type='button'
                        onClick={closeModal}
                        className='bg-yellow-gradient btn text-[#292929] font-medium rounded-full px-5 py-2 text-sm'
                      >
                        Close
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <p className='mt-3 text-sm text-[#6B6B6B] leading-relaxed'>
                      Contacted by a company claiming to be a Wazuh partner?
                      Send us their name and partner ID and we will confirm
                      whether they are official. We will check the details
                      against our partner registry and reply directly.
                    </p>
                    <Formik
                      initialValues={initialValues}
                      validationSchema={validationSchema}
                      onSubmit={handleSubmit}
                    >
                      {({ errors, touched, isSubmitting }) => (
                        <Form className='mt-6'>
                          <label className='block text-sm font-medium text-[#1A1A1A] mb-2'>
                            Partner name{' '}
                            <span className='text-[#3D82F1]'>*</span>
                          </label>
                          <Field
                            name='partnerName'
                            type='text'
                            placeholder='Company name as it was given to you'
                            className='w-full rounded-lg border border-gray-300 text-sm text-gray-900 p-2.5 focus:ring-[#3D82F1] focus:border-[#3D82F1]'
                          />
                          {errors.partnerName && touched.partnerName && (
                            <p className='text-red-500 text-sm mt-1'>
                              {errors.partnerName}
                            </p>
                          )}

                          <label className='block text-sm font-medium text-[#1A1A1A] mt-4 mb-2'>
                            Partner ID{' '}
                            <span className='text-[#3D82F1]'>*</span>
                          </label>
                          <Field
                            name='partnerId'
                            type='text'
                            placeholder='e.g. WZ-PT-04821'
                            className='w-full rounded-lg border border-gray-300 text-sm text-gray-900 p-2.5 focus:ring-[#3D82F1] focus:border-[#3D82F1]'
                          />
                          <p className='text-xs text-[#8A8A8A] mt-1'>
                            Found on their partner certificate or signature
                            block.
                          </p>
                          {errors.partnerId && touched.partnerId && (
                            <p className='text-red-500 text-sm mt-1'>
                              {errors.partnerId}
                            </p>
                          )}

                          <label className='block text-sm font-medium text-[#1A1A1A] mt-4 mb-2'>
                            Your Name{' '}
                            <span className='text-[#3D82F1]'>*</span>
                          </label>
                          <Field
                            name='contactName'
                            type='text'
                            placeholder='Your full name'
                            className='w-full rounded-lg border border-gray-300 text-sm text-gray-900 p-2.5 focus:ring-[#3D82F1] focus:border-[#3D82F1]'
                          />
                          {errors.contactName && touched.contactName && (
                            <p className='text-red-500 text-sm mt-1'>
                              {errors.contactName}
                            </p>
                          )}

                          <label className='block text-sm font-medium text-[#1A1A1A] mt-4 mb-2'>
                            Your Phone number{' '}
                            <span className='text-[#3D82F1]'>*</span>
                          </label>
                          <Field
                            name='phone'
                            type='tel'
                            placeholder='e.g. +1 555 000 0000'
                            className='w-full rounded-lg border border-gray-300 text-sm text-gray-900 p-2.5 focus:ring-[#3D82F1] focus:border-[#3D82F1]'
                          />
                          {errors.phone && touched.phone && (
                            <p className='text-red-500 text-sm mt-1'>
                              {errors.phone}
                            </p>
                          )}

                          <label className='block text-sm font-medium text-[#1A1A1A] mt-4 mb-2'>
                            Country{' '}
                            <span className='text-[#3D82F1]'>*</span>
                          </label>
                          <Field
                            as='select'
                            name='country'
                            className='w-full rounded-lg border border-gray-300 text-sm text-gray-900 p-2.5 focus:ring-[#3D82F1] focus:border-[#3D82F1]'
                          >
                            <CountriesList />
                          </Field>
                          {errors.country && touched.country && (
                            <p className='text-red-500 text-sm mt-1'>
                              {errors.country}
                            </p>
                          )}

                          <label className='block text-sm font-medium text-[#1A1A1A] mt-4 mb-2'>
                            Your work email{' '}
                            <span className='text-[#3D82F1]'>*</span>
                          </label>
                          <Field
                            name='email'
                            type='email'
                            placeholder='you@company.com'
                            className='w-full rounded-lg border border-gray-300 text-sm text-gray-900 p-2.5 focus:ring-[#3D82F1] focus:border-[#3D82F1]'
                          />
                          {errors.email && touched.email && (
                            <p className='text-red-500 text-sm mt-1'>
                              {errors.email}
                            </p>
                          )}

                          {submitError && (
                            <p className='text-red-500 text-sm mt-3'>
                              {typeof submitError === 'string'
                                ? submitError
                                : 'Could not send the verification request.'}
                            </p>
                          )}

                          <div className='mt-8 flex justify-end gap-3'>
                            <button
                              type='button'
                              onClick={closeModal}
                              className='rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-[#1A1A1A] hover:bg-gray-50'
                            >
                              Cancel
                            </button>
                            <button
                              type='submit'
                              disabled={isSubmitting}
                              className='bg-yellow-gradient btn text-[#292929] font-medium rounded-lg px-5 py-2 text-sm disabled:opacity-60'
                            >
                              Send request &gt;
                            </button>
                          </div>
                        </Form>
                      )}
                    </Formik>
                  </>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default VerifyPartnerModal;
